import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { omit } from 'lodash';
import { AxiosResponse } from 'axios';

const createOrganizationRequest = async (
  props: paths['/organization']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/organization']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/organization']['post']['responses']['201']['content']['application/json']
    >
  >(`/organization`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateOrganizationMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['organization'],
    mutationFn: (
      props: paths['/organization']['post']['requestBody']['content']['application/json'],
    ) => createOrganizationRequest(props),
    onSuccess: () => client.invalidateQueries({ queryKey: ['organizations'] }),
  });

  return mutation;
};

const editOrganizationRequest = async (
  props: paths['/organization/{record_id}']['patch']['requestBody']['content']['application/json'] & {
    id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/organization/{record_id}']['patch']['requestBody']['content']['application/json']
  >(`/organization/${props.id}`, omit({ ...props }, 'id'));
  const data = await response;

  return data;
};

export const useEditOrganizationMutation = (organization_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['organization'],
    mutationFn: (
      props: paths['/organization/{record_id}']['patch']['requestBody']['content']['application/json'] & {
        id: string;
      },
    ) => editOrganizationRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['organizations'] }),
        client.invalidateQueries({
          queryKey: ['organization', organization_id],
        }),
      ]),
  });

  return mutation;
};

const uploadOrganizationLogoRequest = async (props: {
  id: string;
  file: FormData;
}) => {
  const response = axiosInstance.put<{
    id: string;
    file: FormData;
  }>(`/organization/${props.id}/logo`, props.file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  const data = await response;

  return data;
};

export const useUploadOrganizationLogoMutation = (organization_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['organizationLogoUpload'],
    mutationFn: (props: { id: string; file: FormData }) =>
      uploadOrganizationLogoRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['organizations'] }),
        client.invalidateQueries({
          queryKey: ['organization', organization_id],
        }),
      ]),
  });

  return mutation;
};

const deleteOrganizationLogoRequest = async (props: { id: string }) => {
  const response = axiosInstance.delete<{
    id: string;
  }>(`/organization/${props.id}/logo`);
  const data = await response;

  return data;
};

export const useDeleteOrganizationLogoMutation = (organization_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteOrganizationLogo'],
    mutationFn: (props: { id: string }) => deleteOrganizationLogoRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['organizations'] }),
        client.invalidateQueries({
          queryKey: ['organization', organization_id, 'logo'],
        }),
      ]),
  });

  return mutation;
};

export const organizationsCsvDownloadRequest = async (
  props: paths['/record/organizations/csv']['get']['parameters']['query'],
) => {
  const response = await axiosInstance.get(`/record/organizations/csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
    params: props,
  });

  const data = (await response).data;

  return data;
};

export const useOrganizationsCsvDownloadQuery = () => {
  const mutation = useMutation({
    mutationKey: ['organizationsCSVDownload'],
    mutationFn: (
      props: paths['/record/organizations/csv']['get']['parameters']['query'],
    ) => organizationsCsvDownloadRequest(props),
  });

  return mutation;
};
